import { cloneDeep } from 'lodash';

import { AllResponseTypes, OtherPayloadTypes } from './dynamic-route-helpers';
import { RETRIEVE_ROUTE_MAP } from './route-map';

/**
 * Abstracts the process of updating source data in the base ROUTE_MAP object according to mutation HTTP requests.
 * Note that the path provided to this method should be that of the _data source_, not that of the
 * request responsible for updating the source (which is likely different).
 *
 * @param sourcePath - The path in `ROUTE_MAP.GET` for the source data.
 * @param newValues - The values to update.
 * @param sourceTransformer - [optional] Function to transform the source data to what's needed to update. Especially
 *                            useful in the case of updates to arrays of objects.
 */
export function updateSourceDataEntry<T extends AllResponseTypes>(
  sourcePath: string,
  newValues: Partial<T> | Partial<T>[keyof T] | Partial<Partial<T>[keyof T]>,
  sourceTransformer?: (source: T) => T
): T {
  const source = RETRIEVE_ROUTE_MAP[sourcePath] as T | undefined;
  if (!source) throw new Error(`Invalid source path: path ${sourcePath} likely does not exist for method GET`);

  const clonedSource = cloneDeep(source);
  const clonedSourceValue = sourceTransformer ? sourceTransformer(clonedSource) : clonedSource;
  const updatedSource = { ...clonedSourceValue, ...newValues } as T;
  RETRIEVE_ROUTE_MAP[sourcePath] = Array.isArray(clonedSource) ? clonedSource : updatedSource;
  return updatedSource;
}

/**
 * Generates a random number using the browser's crypto API.
 * This is safer than using Math.random() because Math.random() is not cryptographically secure.
 * @returns A random number between 0 and 1.
 *
 */
const getRandomNumber = () => {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return array[0] / 2 ** 32;
};

/**
 * Abstracts the process of creating source data in the base ROUTE_MAP object according to mutation HTTP requests.
 * Note that the path provided to this method should be that of the _data source_, not that of the
 * request responsible for updating the source (which is likely different).
 *
 * @param sourcePath - The path in `ROUTE_MAP` for the source data.
 * @param newValues - The values to update.
 * @param pk - The primary key property name of the object, auto-generated here.
 */
export function createSourceDataEntry(sourcePath: string, newValues?: OtherPayloadTypes, pk = 'id'): AllResponseTypes {
  const source = RETRIEVE_ROUTE_MAP[sourcePath];
  if (!source) throw new Error(`Invalid source path: path ${sourcePath} likely does not exist for method GET`);
  const clonedSource = cloneDeep(source) as AllResponseTypes;
  let newClone: AllResponseTypes;
  const newValuesObject = { ...(newValues ?? {}), [pk]: Math.round(getRandomNumber() * 99999) };
  if (Array.isArray(clonedSource)) {
    newClone = [...clonedSource, newValuesObject] as AllResponseTypes;
  } else {
    newClone = { ...clonedSource, ...newValuesObject } as AllResponseTypes;
  }
  RETRIEVE_ROUTE_MAP[sourcePath] = newClone;
  return newClone;
}
